import { Globe, SlidersHorizontal } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SingleValue } from "react-select";
import DateRangePickerButton from "../../../components/DateRangePicker/DateRangePickerButton";
import SearchInput from "../../../components/SearchInput";

import { Button } from "flowbite-react";
import AnimatedModal from "../../../components/AnimatedModal";
import CustomSwitch from "../../../components/CustomSwitch";
import { useDateRange } from "../../../contexts/DateRangeContext";
import {
  useAuth,
  useCampaignCountryCost,
  useCountryReports,
  useIsMobile,
} from "../../../hooks";
import { useCountryChartData } from "../../../hooks/get/useChartData";
import i18n from "../../../i18n";
import countryData from "../../../shared/country.json";
import { IDateRangePreset, ISelectOption, StatusOptionData } from "../../../interfaces";
import { checkPermission } from "../../../lib/utils";
import { LOCAL_STORAGE_KEYS, PERMISSION_MAPPER } from "../../../shared/constants";
import AppRevenueChart from "../../Apps/components/AppRevenueChart";
import CampaignCountryCostModal from "../../Apps/components/CampaignCountryCostModal";
import { Filters } from "../components/Filters";
import { ReportsTable } from "../components/ReportsTable";
import { AdmobApp, AppOption } from "../interface";
import { dynamicConstants } from "../../../shared/dynamicConstants";
import toast from "react-hot-toast";

const columnIds = {
  APP_NAME: "app_name",
  COUNTRY: "country",
  ACCOUNT: "user_name",
  ESTIMATE_REVENUE: "estimate_revenue",
  IN_APP_PURCHASES_OR_SUBSCRIPTIONS: "developer_proceeds",
  COST_DOLLAR: "without_tax.total_cost_usd",
  COST_INR: "without_tax.total_campaign_cost_inr",
  NET_DOLLAR: "without_tax.total_net",
  PERCENTAGE: "without_tax.net_percentage",
  LAST_BID_CURRENT_BID_RUPEE: "bid",
  COST_DOLLAR_WITH_TAX: "with_tax.total_cost_usd",
  COST_INR_WITH_TAX: "with_tax.total_campaign_cost_inr",
  NET_DOLLAR_WITH_TAX: "with_tax.total_net",
  PERCENTAGE_WITH_TAX: "with_tax.net_percentage",
  ACTIONS: "actions",
};

const withTaxColumnIds = [
  columnIds.COST_DOLLAR_WITH_TAX,
  columnIds.COST_INR_WITH_TAX,
  columnIds.NET_DOLLAR_WITH_TAX,
  columnIds.PERCENTAGE_WITH_TAX,
];

// Add this new component at the top of the file, after imports
const FilterSummary: React.FC<{
  selectedApp: AppOption | null;
  selectedCountry: ISelectOption | null;
  selectedStatus: StatusOptionData | null;
  selectedPlatform: ISelectOption | null;
  recentBidUpdate: boolean;
  onClearFilter: (
    type: "app" | "country" | "status" | "platform" | "bid"
  ) => void;
  onResetAll: () => void;
  t: (key: string) => string;
}> = ({
  selectedApp,
  selectedCountry,
  selectedStatus,
  selectedPlatform,
  recentBidUpdate,
  onClearFilter,
  onResetAll,
  t,
}) => {
    // Only show when there are actual filters applied (not default values)
    const hasActiveFilters =
      selectedApp ||
      selectedCountry ||
      (selectedStatus && selectedStatus.value !== "all") ||
      (selectedPlatform &&
        selectedPlatform.value &&
        selectedPlatform.value !== "all") ||
      recentBidUpdate;

    if (!hasActiveFilters) return null;
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 p-2 bg-gray-50 rounded-md ">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600">
            {t("common.appliedFilters")}:
          </span>
          {selectedApp && (
            <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-gray-200">
              <span className="text-sm text-gray-700">{selectedApp.label}</span>
              <button
                onClick={() => onClearFilter("app")}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          )}
          {selectedCountry && (
            <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-gray-200">
              <span className="text-sm text-gray-700">
                {selectedCountry.label}
              </span>
              <button
                onClick={() => onClearFilter("country")}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          )}
          {selectedStatus && selectedStatus.value !== "all" && (
            <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-gray-200">
              <span className="text-sm text-gray-700">
                {selectedStatus.label}
              </span>
              <button
                onClick={() => onClearFilter("status")}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          )}
          {selectedPlatform &&
            selectedPlatform.value &&
            selectedPlatform.value !== "all" && (
              <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-gray-200">
                <span className="text-sm text-gray-700">
                  {selectedPlatform.value === "android"
                    ? "Android"
                    : selectedPlatform.value === "ios"
                      ? "iOS"
                      : selectedPlatform.value}
                </span>
                <button
                  onClick={() => onClearFilter("platform")}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            )}
          {recentBidUpdate && (
            <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-gray-200">
              <span className="text-sm text-gray-700">
                {t("appCountryWiseReports.recentBidUpdate")}
              </span>
              <button
                onClick={() => onClearFilter("bid")}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          )}
        </div>
        <button
          onClick={onResetAll}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <span>{t("common.resetAllFilters")}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 011.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    );
  };



const AppCountryReports: React.FC = () => {
  const { t } = useTranslation();
  const { user, updatePreferences } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredReports, setFilteredReports] = useState<AdmobApp[]>([]);
  const [selectedApp, setSelectedApp] = useState<AppOption | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<ISelectOption | null>(
    () => {
      const countryCode = searchParams.get("country");
      return countryCode ? { label: "", value: countryCode } : null;
    }
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [countryCostModal, setCountryCostModal] = useState({
    open: false,
    countryCode: "",
    countryName: "",
    appPackageId: "",
  });

  const [recentBidUpdate, setRecentBidUpdate] = useState(() => {
    return searchParams.get("recentBidUpdate") === "true";
  });

  const { DYNAMIC_STATUS_OPTIONS, growthIndicatorRange } = dynamicConstants();
  const [selectedStatus, setSelectedStatus] = useState<StatusOptionData | null>(
    () => {
      const statusParam = searchParams.get("status");
      return statusParam
        ? DYNAMIC_STATUS_OPTIONS.find((opt) => opt.value === statusParam) ||
        DYNAMIC_STATUS_OPTIONS[0]
        : DYNAMIC_STATUS_OPTIONS[0];
    }
  );
  const [selectedPlatform, setSelectedPlatform] =
    useState<ISelectOption | null>(() => {
      const platform = searchParams.get("platform");
      if (!platform) return null;
      const allowed = ["android", "ios", "all"];
      if (allowed.includes(platform)) {
        return {
          label:
            platform === "android"
              ? "Android"
              : platform === "ios"
                ? "iOS"
                : platform,
          value: platform,
        };
      }
      return null;
    });
  const [openFilter, setOpenFilter] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { dateRange, selectedPreset, setDateRange, setSelectedPreset } =
    useDateRange();

  const [showChartModal, setShowChartModal] = useState(false);
  const [selectedAppForChart, setSelectedAppForChart] =
    useState<AdmobApp | null>(null);
  const [selectedPresetForChart, setSelectedPresetForChart] = useState<
    IDateRangePreset | undefined
  >(selectedPreset);
  const [chartDateRange, setChartDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>(() => {
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
  });

  // Use React Query hook to fetch country reports
  const { data: countryReportsData, isLoading } = useCountryReports({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    delay: 500,
  });

  // Extract data from the query result
  const allReports = useMemo(
    () => countryReportsData?.admob_apps || [],
    [countryReportsData]
  );
  const apps = useMemo(
    () => countryReportsData?.apps || [],
    [countryReportsData]
  );
  const averageUsdRate = useMemo(
    () => countryReportsData?.average_actual_usd || "",
    [countryReportsData]
  );

  const applyFilters = useCallback(
    (clear = false) => {
      if (clear) return setFilteredReports(allReports);

      let filtered = [...allReports];

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (report) =>
            report.app_name.toLowerCase().includes(query) ||
            report.country.toLowerCase().includes(query)
        );
      }

      // Apply app filter
      if (selectedApp) {
        filtered = filtered.filter(
          (report) => report.app_slug === selectedApp.appId
        );
      }

      // Apply country filter
      if (selectedCountry) {
        filtered = filtered.filter(
          (report) => report.country_code === selectedCountry.value
        );
      }

      // Apply platform filter
      if (
        selectedPlatform &&
        selectedPlatform.value &&
        selectedPlatform.value !== "all"
      ) {
        filtered = filtered.filter(
          (report) =>
            (report.platform || "").toLowerCase() ===
            String(selectedPlatform.value).toLowerCase()
        );
      }

      // Apply status filter
      if (selectedStatus && selectedStatus.value !== "all") {
        filtered = filtered.filter((report) => {
          const percentage = (report.without_tax?.net_percentage as number) * 100; // Multiply by 100 before comparison
          const [minThreshold, maxThreshold] = growthIndicatorRange;
          switch (selectedStatus.value) {
            case "success":
              return percentage > maxThreshold;
            case "warning":
              return percentage >= minThreshold && percentage <= maxThreshold;
            case "danger":
              return percentage < minThreshold;
            default:
              return true;
          }
        });
      }

      // Apply recent bid update filter
      if (recentBidUpdate) {
        // First filter by campaign_bid_update_on flag
        filtered = filtered.filter(
          (report) => report.campaign_bid_update_on === true
        );

        // Then sort by most recent bid update
        filtered.sort((a, b) => {
          const getLatestUpdateTime = (report: AdmobApp) => {
            if (
              !report.bid_data ||
              !Array.isArray(report.bid_data) ||
              report.bid_data.length === 0
            ) {
              return new Date(0);
            }

            // Find the most recent bid update
            const latestBid = report.bid_data.reduce((latest, current) => {
              if (!current.campaign_bid_updated_on) return latest;
              const currentDate = new Date(current.campaign_bid_updated_on);
              const latestDate = new Date(latest.campaign_bid_updated_on || 0);
              return currentDate > latestDate ? current : latest;
            }, report.bid_data[0]);

            return new Date(latestBid.campaign_bid_updated_on || 0);
          };

          const timeA = getLatestUpdateTime(a);
          const timeB = getLatestUpdateTime(b);

          return timeB.getTime() - timeA.getTime();
        });
      }

      setFilteredReports(filtered);
      setOpenFilter(false);
    },
    [
      allReports,
      searchQuery,
      selectedApp,
      selectedCountry,
      selectedPlatform,
      selectedStatus,
      recentBidUpdate,
      growthIndicatorRange,
    ]
  );

  // Apply client-side filters whenever filter criteria changes
  useEffect(() => {
    applyFilters();
  }, [
    searchQuery,
    allReports,
    selectedApp,
    selectedCountry,
    recentBidUpdate,
    selectedStatus,
    selectedPlatform,
    applyFilters,
  ]);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleAppChange = useCallback(
    (newValue: SingleValue<AppOption>) => {
      setSelectedApp(newValue);
      const params = searchParams;
      if (newValue) {
        params.set("app", newValue.appId);
      } else {
        params.delete("app");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleCountryChange = useCallback(
    (newValue: SingleValue<ISelectOption>) => {
      setSelectedCountry(newValue);
      const params = searchParams;
      if (newValue) {
        params.set("country", newValue.value);
      } else {
        params.delete("country");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handlePlatformChange = useCallback(
    (newValue: SingleValue<ISelectOption>) => {
      setSelectedPlatform(newValue as ISelectOption | null);
      const params = searchParams;
      if (
        newValue &&
        (newValue as ISelectOption).value &&
        (newValue as ISelectOption).value !== "all"
      ) {
        params.set("platform", (newValue as ISelectOption).value as string);
      } else {
        params.delete("platform");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleStatusChange = useCallback(
    (newValue: SingleValue<StatusOptionData>) => {
      setSelectedStatus((newValue as StatusOptionData) || DYNAMIC_STATUS_OPTIONS[0]);
      const params = searchParams;
      if (newValue && newValue.value !== "all") {
        params.set("status", newValue.value);
      } else {
        params.delete("status");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleRecentBidUpdate = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRecentBidUpdate(e.target.checked);
      const params = searchParams;
      if (e.target.checked) {
        params.set("recentBidUpdate", "true");
      } else {
        params.delete("recentBidUpdate");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleApplyFilter = useCallback(() => {
    setOpenFilter(false);
    applyFilters();
  }, [applyFilters]);

  const handleResetFilter = useCallback(() => {
    setSelectedApp(null);
    setSelectedCountry(null);
    setSelectedStatus(DYNAMIC_STATUS_OPTIONS[0]);
    setSelectedPlatform(null);
    setRecentBidUpdate(false);
    setSearchQuery("");
    setOpenFilter(false);
    const params = new URLSearchParams(searchParams);
    params.delete("app");
    params.delete("country");
    params.delete("status");
    params.delete("platform");
    params.delete("recentBidUpdate");
    setSearchParams(params);
    applyFilters(true);
  }, [applyFilters, searchParams, setSearchParams]);

  const handleDateRangeChange = useCallback(
    (startDate: Date, endDate: Date) => {
      localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_FROM, startDate.toISOString());
      localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_TO, endDate.toISOString());
      setDateRange(startDate, endDate);
    },
    [setDateRange]
  );

  const handlePresetChange = useCallback(
    (preset: IDateRangePreset | undefined) => {
      localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET, preset?.id || "");
      setSelectedPreset(preset);
    },
    [setSelectedPreset]
  );

  // Use React Query hook to fetch campaign country cost
  const {
    data: campaignCountryCostData,
    isLoading: isLoadingCampaignCountryCost,
  } = useCampaignCountryCost({
    appPackageId: countryCostModal.appPackageId,
    countryCode: countryCostModal.countryCode,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const handleCountryClick = useCallback(
    (countryCode: string, countryName: string, appPackageId: string) => {
      setCountryCostModal({
        open: true,
        countryCode,
        countryName,
        appPackageId,
      });
    },
    []
  );

  // Initialize selectedApp when apps data is loaded
  useEffect(() => {
    const appSlug = searchParams.get("app");
    if (!appSlug || apps.length === 0) return;

    const app = apps.find((a) => a.app_slug === appSlug);
    if (!app) return;

    setSelectedApp({
      label: app.app_name,
      value: app.app_slug,
      platform: app.app_platform,
      appId: app.app_slug,
      app_logo_url: app.app_logo_url,
      app_platform: app.app_platform,
    });
  }, [apps, searchParams, setSearchParams]);

  // Update selectedCountry label after reports load or country param changes
  useEffect(() => {
    const countryCode = searchParams.get("country");
    const country = countryData.find((r) => r.value === countryCode);
    setSelectedCountry(country || null);
  }, [allReports, searchParams]);

  // Initialize platform from URL param
  useEffect(() => {
    const platform = searchParams.get("platform");
    if (!platform) {
      setSelectedPlatform(null);
      return;
    }
    const allowed = ["android", "ios", "all"];
    if (allowed.includes(platform)) {
      setSelectedPlatform({
        label:
          platform === "android"
            ? "Android"
            : platform === "ios"
              ? "iOS"
              : platform,
        value: platform,
      });
    } else {
      setSelectedPlatform(null);
    }
  }, [searchParams]);

  // Initialize status filter from URL param
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      const status = DYNAMIC_STATUS_OPTIONS.find((opt) => opt.value === statusParam);
      if (status) {
        setSelectedStatus(status);
      }
    }
  }, [searchParams]);

  const handleClearFilter = useCallback(
    (type: "app" | "country" | "status" | "platform" | "bid") => {
      const params = new URLSearchParams(searchParams);
      switch (type) {
        case "app":
          setSelectedApp(null);
          params.delete("app");
          setSearchParams(params);
          break;
        case "country":
          setSelectedCountry(null);
          params.delete("country");
          setSearchParams(params);
          break;
        case "status":
          setSelectedStatus(DYNAMIC_STATUS_OPTIONS[0]);
          params.delete("status");
          setSearchParams(params);
          break;
        case "platform":
          setSelectedPlatform(null);
          params.delete("platform");
          setSearchParams(params);
          break;
        case "bid":
          setRecentBidUpdate(false);
          params.delete("recentBidUpdate");
          setSearchParams(params);
          break;
      }
    },
    [searchParams, setSearchParams]
  );

  const isFilterApplied = useMemo(() => {
    const hasStatus = selectedStatus && selectedStatus.value !== "all";
    const hasPlatform =
      selectedPlatform &&
      selectedPlatform.value &&
      selectedPlatform.value !== "all";
    return !!(
      selectedApp ||
      selectedCountry ||
      hasStatus ||
      hasPlatform ||
      recentBidUpdate
    );
  }, [
    selectedApp,
    selectedCountry,
    selectedStatus,
    selectedPlatform,
    recentBidUpdate,
  ]);

  const hasAppDetailsReadPermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.APP_FIND_ONE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const hasCountryCostReadPermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.CAMPAIGN_COUNTRY_COST_READ,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const hasCampaignBidReadPermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.BID_MANAGEMENT_READ,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const hasAppChartReadPermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.APP_DETAIL_CHART_READ,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  // const [withTax, setWithTax] = useState(false);
  const appCountryReportsPreference = useMemo(
    () => user?.preferences?.app_country_report,
    [user]
  );
  const columnConfig = useMemo(
    () => appCountryReportsPreference?.column_config ?? {},
    [appCountryReportsPreference]
  );

  const getColumnLabel = useCallback(
    (id: string) => {
      switch (id) {
        case columnIds.APP_NAME:
          return t("appCountryWiseReports.columns.name");
        case columnIds.COUNTRY:
          return t("appCountryWiseReports.columns.country");
        case columnIds.ACCOUNT:
          return t("appCountryWiseReports.columns.account");
        case columnIds.ESTIMATE_REVENUE:
          return t("appCountryWiseReports.columns.estimateRevenue");
        case columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS:
          return t(
            "appCountryWiseReports.columns.inAppPurchasesOrSubscriptions"
          );
        case columnIds.COST_DOLLAR:
          return t("appCountryWiseReports.columns.totalCost");
        case columnIds.COST_INR:
          return t("appCountryWiseReports.columns.totalCostInr");
        case columnIds.NET_DOLLAR:
          return t("appCountryWiseReports.columns.netDollar");
        case columnIds.PERCENTAGE:
          return t("appCountryWiseReports.columns.percentage");
        case columnIds.LAST_BID_CURRENT_BID_RUPEE:
          return t("appCountryWiseReports.columns.lastBidCurrentBid");
        case columnIds.COST_DOLLAR_WITH_TAX:
          return t("appCountryWiseReports.columns.totalCostWithTax");
        case columnIds.COST_INR_WITH_TAX:
          return t("appCountryWiseReports.columns.totalCostDollarWithTax");
        case columnIds.NET_DOLLAR_WITH_TAX:
          return t("appCountryWiseReports.columns.netDollarWithTax");
        case columnIds.PERCENTAGE_WITH_TAX:
          return t("appCountryWiseReports.columns.percentageWithTax");
        case columnIds.ACTIONS:
          return t("apps.columns.actions");
        default:
          return id;
      }
    },
    [t]
  );

  const showWithTax = useMemo(
    () =>
      Object.keys(columnConfig).length > 0
        ? Object.entries(columnConfig)
          .filter(([key]) => withTaxColumnIds.includes(key))
          .every(([key]) => columnConfig[key] === true)
        : false,
    [columnConfig]
  );
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [columnOptions, setColumnOptions] = useState([
    {
      id: columnIds.APP_NAME,
      label: t("appCountryWiseReports.columns.name"),
      enabled: columnConfig[columnIds.APP_NAME] ?? true,
    },
    {
      id: columnIds.COUNTRY,
      label: t("appCountryWiseReports.columns.country"),
      enabled: columnConfig[columnIds.COUNTRY] ?? true,
    },
    {
      id: columnIds.ACCOUNT,
      label: t("appCountryWiseReports.columns.account"),
      enabled: columnConfig[columnIds.ACCOUNT] ?? true,
    },
    {
      id: columnIds.ESTIMATE_REVENUE,
      label: t("appCountryWiseReports.columns.estimateRevenue"),
      enabled: columnConfig[columnIds.ESTIMATE_REVENUE] ?? true,
    },
    {
      id: columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS,
      label: t("appCountryWiseReports.columns.inAppPurchasesOrSubscriptions"),
      enabled:
        columnConfig[columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS] ?? true,
    },
    {
      id: columnIds.COST_DOLLAR,
      label: t("appCountryWiseReports.columns.totalCost"),
      enabled: columnConfig[columnIds.COST_DOLLAR] ?? true,
    },
    {
      id: columnIds.COST_INR,
      label: t("appCountryWiseReports.columns.totalCostInr"),
      enabled: columnConfig[columnIds.COST_INR] ?? true,
    },
    {
      id: columnIds.NET_DOLLAR,
      label: t("appCountryWiseReports.columns.netDollar"),
      enabled: columnConfig[columnIds.NET_DOLLAR] ?? true,
    },
    {
      id: columnIds.PERCENTAGE,
      label: t("appCountryWiseReports.columns.percentage"),
      enabled: columnConfig[columnIds.PERCENTAGE] ?? true,
    },
    {
      id: columnIds.LAST_BID_CURRENT_BID_RUPEE,
      label: t("appCountryWiseReports.columns.lastBidCurrentBid"),
      enabled: columnConfig[columnIds.LAST_BID_CURRENT_BID_RUPEE] ?? true,
    },
    {
      id: columnIds.COST_DOLLAR_WITH_TAX,
      label: t("appCountryWiseReports.columns.totalCostWithTax"),
      enabled: columnConfig[columnIds.COST_DOLLAR_WITH_TAX] ?? false,
    },
    {
      id: columnIds.COST_INR_WITH_TAX,
      label: t("appCountryWiseReports.columns.totalCostDollarWithTax"),
      enabled: columnConfig[columnIds.COST_INR_WITH_TAX] ?? false,
    },
    {
      id: columnIds.NET_DOLLAR_WITH_TAX,
      label: t("appCountryWiseReports.columns.netDollarWithTax"),
      enabled: columnConfig[columnIds.NET_DOLLAR_WITH_TAX] ?? false,
    },
    {
      id: columnIds.PERCENTAGE_WITH_TAX,
      label: t("appCountryWiseReports.columns.percentageWithTax"),
      enabled: columnConfig[columnIds.PERCENTAGE_WITH_TAX] ?? false,
    },
    {
      id: columnIds.ACTIONS,
      label: t("apps.columns.actions"),
      enabled: columnConfig[columnIds.ACTIONS] ?? true,
    },
  ]);

  useEffect(() => {
    setColumnOptions((prev) =>
      prev.map((col) => ({ ...col, label: getColumnLabel(col.id) }))
    );
  }, [getColumnLabel]);

  const handleColumnToggle = useCallback(
    (columnIds: string[], checked?: boolean) => {
      const clonedColumnConfig = [...columnOptions];
      for (let i = 0; i < clonedColumnConfig.length; i++) {
        if (columnIds.includes(clonedColumnConfig[i].id)) {
          clonedColumnConfig[i].enabled =
            checked !== undefined ? checked : !clonedColumnConfig[i].enabled;
        }
      }
      setColumnOptions(clonedColumnConfig);

      updatePreferences({
        app_country_report: {
          ...appCountryReportsPreference,
          column_config: clonedColumnConfig.reduce((acc, col) => {
            acc[col.id] = col.enabled;
            return acc;
          }, {} as Record<string, boolean>),
        },
      });
    },
    [columnOptions, appCountryReportsPreference, updatePreferences]
  );

  // Only pass enabled columns to ReportsTable
  const enabledColumnIds = useMemo(
    () => columnOptions.filter((c) => c.enabled).map((c) => c.id),
    [columnOptions]
  );

  const { data: chartData, isLoading: isLoadingChartData, error: chartError } =
    useCountryChartData({
      appSlug: selectedAppForChart?.app_slug || "",
      startDate: chartDateRange.startDate,
      endDate: chartDateRange.endDate,
      countryCode: selectedAppForChart?.country_code || "",
    });

  useEffect(() => {
    if (showChartModal && chartError) {
      toast.error(chartError.message);
      setShowChartModal(false);
    }
  }, [chartError, showChartModal]);

  const handleChartClick = useCallback(
    (app: AdmobApp) => {
      setSelectedAppForChart(app);
      setShowChartModal(true);
      setSelectedPresetForChart(selectedPreset);
      setChartDateRange({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
    },
    [selectedPreset, dateRange.startDate, dateRange.endDate]
  );

  const handleChartDateRangeChange = useCallback(
    (startDate: Date, endDate: Date) => {
      setChartDateRange({ startDate, endDate });
    },
    []
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-2 mb-2">
        <div>
          <div className="flex items-center gap-2">
            <Globe size={20} className="text-gray-700" weight="duotone" />
            <h1 className="text-md sm:text-xl font-bold text-gray-700">
              {t("appCountryWiseReports.title")}
            </h1>
          </div>
          {isLoading ? (
            <div className="w-[200px] h-4 rounded bg-gray-200 animate-pulse mt-1"></div>
          ) : (
            <>
              {averageUsdRate && (
                <div className="text-xs text-gray-600 mt-1">
                  {`${t(
                    "appCountryWiseReports.averageCurrencyRate"
                  )} = ₹${averageUsdRate}`}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Filters
              key={"unified-filters"}
              t={t}
              selectedApp={selectedApp}
              selectedCountry={selectedCountry}
              recentBidUpdate={recentBidUpdate}
              apps={apps}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onAppChange={handleAppChange}
              onCountryChange={handleCountryChange}
              onRecentBidUpdate={handleRecentBidUpdate}
              onApplyFilter={handleApplyFilter}
              onResetFilter={handleResetFilter}
              onPresetChange={handlePresetChange}
              selectedStatus={selectedStatus}
              onStatusChange={handleStatusChange}
              selectedPlatform={selectedPlatform}
              onPlatformChange={handlePlatformChange}
              growthIndicatorRange={growthIndicatorRange}
              // These props were in mobile only, ensuring they are available if needed by the component
              onDateRangeChange={handleDateRangeChange}
              dateRange={dateRange}
              selectedPreset={selectedPreset?.id}
            />
            <DateRangePickerButton
              onDateRangeChange={handleDateRangeChange}
              onPresetChange={handlePresetChange}
              initialStartDate={dateRange.startDate}
              initialEndDate={dateRange.endDate}
              initialPreset={selectedPreset?.id}
            />
          </div>
          <div className="flex items-center gap-2">
            <SearchInput
              onSearch={handleSearch}
              className="w-full sm:w-[250px]"
              style={{ height: "38px" }}
              size="md"
            />
            <Button
              color="light"
              size="sm"
              onClick={() => setShowColumnSelector(true)}
              className="min-w-[38px] h-[38px]"
            >
              <SlidersHorizontal size={20} />
            </Button>
            <CustomSwitch
              checked={showWithTax}
              onChange={(checked) => {
                handleColumnToggle(withTaxColumnIds, checked);
              }}
              label={t("common.withTax")}
              color="indigo"
            />
          </div>
        </div>
      </div>

      {/* Add Filter Summary */}
      <FilterSummary
        selectedApp={selectedApp}
        selectedCountry={selectedCountry}
        selectedStatus={selectedStatus}
        selectedPlatform={selectedPlatform}
        recentBidUpdate={recentBidUpdate}
        onClearFilter={handleClearFilter}
        onResetAll={handleResetFilter}
        t={t}
      />

      <AnimatedModal
        show={showColumnSelector}
        onClose={() => setShowColumnSelector(false)}
        title={
          t("appCountryWiseReports.columnSelectorTitle") || "Select Columns"
        }
        size="sm"
      >
        <div className="space-y-4 p-4">
          {columnOptions.map((column) => (
            <div key={column.id} className="flex items-center justify-between">
              <span>{column.label}</span>
              <CustomSwitch
                checked={column.enabled}
                onChange={() => handleColumnToggle([column.id])}
                size="sm"
              />
            </div>
          ))}
        </div>
      </AnimatedModal>
      <ReportsTable
        data={filteredReports}
        isLoading={isLoading}
        t={t}
        handleCountryClick={handleCountryClick}
        navigate={navigate}
        showPagination={true}
        isMobile={isMobile}
        isFilterApplied={isFilterApplied}
        hasCampaignBidManagementPermission={hasCampaignBidReadPermission}
        hasCountryCostReadPermission={hasCountryCostReadPermission}
        hasAppDetailsReadPermission={hasAppDetailsReadPermission}
        enabledColumnIds={enabledColumnIds}
        withTax={showWithTax}
        hasAppChartReadPermission={hasAppChartReadPermission}
        onChartClick={handleChartClick}
        columnIds={columnIds}
        currentLanguage={i18n.language}
        growthIndicatorRange={growthIndicatorRange}
      />

      <CampaignCountryCostModal
        open={countryCostModal.open}
        onClose={() =>
          setCountryCostModal((prev) => ({ ...prev, open: false }))
        }
        loading={isLoadingCampaignCountryCost}
        data={campaignCountryCostData}
        countryName={countryCostModal.countryName}
      />
      {/* Removed BidManagement component */}

      {/* Chart Modal */}
      <AnimatedModal
        show={showChartModal}
        onClose={() => {
          setSelectedAppForChart(null);
          setShowChartModal(false);
          setSelectedPresetForChart(selectedPreset);
          handleChartDateRangeChange(dateRange.startDate, dateRange.endDate);
        }}
        title={`${selectedAppForChart?.app_name || ""} - ${t(
          "apps.columns.estimatedRevenue"
        )} vs ${t("apps.columns.totalCost")}`}
        size="2xl"
        bodyClassName="p-4"
      >
        <AppRevenueChart
          key={`${selectedPresetForChart}|${JSON.stringify(chartDateRange)}`}
          data={chartData || []}
          onDateRangeChange={handleChartDateRangeChange}
          setSelectedPresetForChart={setSelectedPresetForChart}
          loading={isLoadingChartData}
          dateRange={chartDateRange}
          selectedPresetForChart={selectedPresetForChart}
        />
      </AnimatedModal>
    </div>
  );
};

export default AppCountryReports;
